import { Button, Progress } from '@formo/ui';
import { Zap } from 'lucide-react';
import Link from 'next/link';
import { FC } from 'react';
import { USAGE_RESTRICTIONS } from '~/app/context/AccessGateContext';
import useAccessGate from '~/app/hooks/useAccessGate';
import { useTeamTier } from '~/app/hooks/useTeamTier';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
} from '~/components/ui/dialog';

import { capitalizeFirstLetter } from '../../Builder/components/common/helpers';
import { Divider } from '../../common';

export interface UsageRestrictModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  restrictionType?: (typeof USAGE_RESTRICTIONS)[number][];
}

const UsageRestrictModal: FC<UsageRestrictModalProps> = ({
  open,
  setOpen,
  restrictionType,
}) => {
  const { activeTeamTier } = useTeamTier();
  const { limitations } = useAccessGate();

  const geLabel = (type: (typeof USAGE_RESTRICTIONS)[number]) => {
    switch (type) {
      case 'MTU':
        return 'Monthly Tracked Users';
      case 'EVENT':
        return 'Monthly Events';
      default:
        return type;
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="mx-auto max-w-[640px] rounded-lg bg-surface-secondary p-6">
        <img className="mx-auto" src="/logo-full.svg" alt="logo" />
        <DialogDescription className="mt-4 text-sm text-content-secondary b2 bg-surface-secondary">
          <Card className="bg-white border-none py-8 px-10 shadow-none space-y-6">
            <div className="text-center h3 text-red-500">
              You've hit your {capitalizeFirstLetter(activeTeamTier.type)}{' '}
              limit.
            </div>
            <div>
              {restrictionType?.map((restrict) => (
                <div key={restrict} className="flex flex-col items-end gap-2">
                  <div className="w-full flex justify-start">
                    {geLabel(restrict)}
                  </div>
                  <Progress
                    value={100}
                    classNames={{
                      indicator: 'bg-orange-500',
                      root: 'bg-cyan-50 w-full',
                    }}
                  />

                  <div className="text-content-secondary">
                    <span className="h4 text-red-500 font-semibold">
                      {limitations?.[restrict]}
                    </span>{' '}
                    out of {limitations?.[restrict]}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-content-secondary">
              <div>When you surpass your monthly usage limit:</div>
              <div className="text-content-primary font-semibold">
                ❌ Formo will stop ingesting new events within 24 hours.
              </div>
              <div className="text-content-primary font-semibold">
                ❌ Events captured beyond the limit will not be processed or
                stored.
              </div>
              <div className="text-content-primary font-semibold">
                ❌ Excess events and users are hidden on the dashboard.
              </div>
            </div>
            <Card className="bg-surface-secondary p-6 flex-col items-center justify-center border-none shadow-none">
              <div className="b2 text-content-secondary text-center">
                <span className="text-content-primary font-semibold">
                  Upgrade
                </span>{' '}
                to get higher limits and unlock all features.
              </div>

              <Link href={`/teams/${activeTeamTier.team?.id}/upgrade`}>
                <Button
                  type="button"
                  className="b3 font-semibold w-full bg-gradient-to-r from-[#A1FF40] to-[#FFC935] mt-4 border-none"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <Zap /> Upgrade now
                </Button>
              </Link>
            </Card>

            <Divider />

            <div className="b3 text-content-secondary">
              If you have any questions, please{' '}
              <Link
                target="_blank"
                href="https://formo.so/support"
                className="text-content-link"
              >
                contact support.
              </Link>
            </div>
          </Card>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};

export default UsageRestrictModal;
